import {API2_CONTACTS} from 'consts/api2.js'
import BaseStore from './base-store.js'
import StoreProxy from './store_proxy.js'


import * as $utils from 'utils';
export class Contact extends BaseStore {
  constructor(values = {}) {
    super({
      id: undefined,
      first_name: undefined,
      last_name: undefined,
      middle_name: undefined,
      linkedin_url: undefined,
      position: undefined,
      created_at: undefined,
      following: false,
      avatar_url: undefined,
      company_name: undefined,
      logo_url: undefined,
      processing_id: undefined,
      signals: undefined,

      tracker_roles: [],
      tracker_alerts: undefined,
      tracker_focus: undefined,
      focus_on: undefined,
      user_company_id: undefined,
      shared_company_id: undefined,
      shared_contact_id: undefined,
      ...values
    })
  }

  get name(){
    return `${this._state.first_name} ${this._state.last_name}`
  }

  get state_copy(){
    const state = $utils.object.deepCopy(this._state)
    Object.keys(state).forEach(key => {
      if (state[key] === undefined) {
        delete state[key]
      }
    })
    return state
  }

  async doSave(rest, changed) {
    const to_write = {}
    for (const key of changed) to_write[key] = this._state[key]
    await rest.patch(`${API2_CONTACTS}/${this.id}`, to_write)
  }

  async drop(userId = undefined) {
    if (userId) {
      await this._rest.delete(`${API2_CONTACTS}/${this.id}?user_id=${userId}`)
      return
    }
    await this._rest.delete(`${API2_CONTACTS}/${this.id}`)
  }
  update(next){
    for (const key in next) {
      if (key === 'avatar_url') {
        const new_val = next[key]?.split('?')[0]
        const prev_val = this._state[key]?.split('?')[0]
        if (new_val === prev_val) continue
      }
      if (key in this._state) {
        this._state[key] = next[key]
      }
    }
  }
  static isUserContact(store){
    return store instanceof Contact
  }
}


export class Contacts extends BaseStore {
  constructor(values = {}) {
    super({
      shared_company_id: undefined,
      view_items: [],
      view_total: 0,
      total: 0,
      ...values
    })
  }

  async doLoad(rest, params) {
    const result = await rest.get(API2_CONTACTS, params)
    const existing_ids = new Set(this._state.view_items.map(i => i.id))
    this._state.view_items = result.view_items.map(
      data => existing_ids.has(data.id) 
        ? this.updateItem(data)
        : new Contact(data))
    this._state.view_total = result.view_total
    this._state.total = result.total
  }

  updateItem(data){
    const contact = this.getContact(data.id)
    if (contact) {
      contact.update(data)
    }
    return contact
  }
  getContact(id) {
    return this._state.view_items.find(x => x.id === id)
  }
  deleteItem(id){
    this._state.view_items = this._state.view_items.filter(contact => contact.id !== id)
  }
  async loadContact(id) {
    const json = await this._rest.get(`${API2_CONTACTS}/${id}`)
    return new Contact(json)
  }

  addContact(data){
    let contact = this.getContact(data.id)
    if (!contact) {
      contact = new Contact(data)
      this._state.view_items.push(contact)
      this._state.total += 1
      this._state.view_total += 1
    }
    else contact.update(data)
  }

  async newContact(params) {
    const data = await this._rest.put(API2_CONTACTS, params)
    return new Contact(data)
  }
  async search(query) {
    return await this._rest.get(`${API2_CONTACTS}/search?query=${encodeURIComponent(query)}`)
  }
}

class ContactsGroups extends BaseStore {
  constructor() {
    super({
      contacts: new Map(),    // sh_company_id -> Contacts
    })
  }

  async doLoad(_, params) {
    const {shared_company_id, ...rest_params} = params
    
    let contacts = this._getContactsStore(shared_company_id)

    if (!contacts) {
      contacts = new Contacts({shared_company_id})
      this._state.contacts.set(shared_company_id, contacts)
    }

    await contacts.load(rest_params)
  
    if (contacts.view_items.length === 0) {
      this._state.contacts.delete(shared_company_id)
    }
  }

  addContact(data){
    const sh_company_id = data.shared_company_id
    let contacts = this._getContactsStore(sh_company_id)
    if (!contacts) {
      contacts = new Contacts({shared_company_id: sh_company_id})
      this._state.contacts.set(sh_company_id, contacts)
    }
    return contacts.addContact(data)
  }

  updateContact(data) {
    this._getContactsStore(data.shared_company_id)
      ?.updateItem(data)
  }

  _getContactsStore(shared_company_id) {
    return this._state.contacts.get(shared_company_id)
  }

  getContacts(shared_company_id) {
    const items = this._getContactsStore(shared_company_id)?.view_items
    return items?.length ? items : undefined
  }

  setContacts(shared_company_id, items) {
    const contacts = this._getContactsStore(shared_company_id)
    if (contacts) {
      const existing = new Map(contacts.view_items.map(contact => [contact.id, contact]))
      contacts.view_items = items.map(data => {
        const contact = existing.get(data.id)
        if (contact) {
          contact.update(data)
          return contact
        }
        return new Contact(data)
      })
    }
    else {
      this._state.contacts.set(
        shared_company_id, new Contacts({shared_company_id, view_items: items.map(i => new Contact(i))})
      )
    }
  }

  async deleteContact(data) {
    const contacts = this._getContactsStore(data.shared_company_id)
    if (contacts) {
      contacts?.deleteItem(data.id)
      if (contacts.view_items.length === 0) {
        this._state.contacts.delete(data.id)
      }
    }
  }
}

export const $contacts_groups = new ContactsGroups()

const contacts = StoreProxy(new Contacts(), {array_prop: 'view_items'})
export default contacts
